<script setup lang="ts">
import RlFileViewer from '@/components/common/upload/RlFileViewer.vue'
import pdfSvg from '@/svg/pdf.svg'
import { VuePDF, usePDF, type PDFInfo } from '@tato30/vue-pdf'
import axios from 'axios'
import type { UploadFile } from 'element-plus'
import { computed, onMounted, ref } from 'vue'

const pdfData = ref<Uint8Array>()
const pdfName = ref<string>()
const { pdf, info } = usePDF(pdfData)

//@ts-expect-error can't get TS to accept the Title property, which I know is there
const documentTitle = computed(() => (info.value as PDFInfo)?.metadata?.info?.Title as string | undefined)
const urlDocumentTitle = computed(() => (props.url ? new URL(props.url).pathname.split('/').pop() : ''))

const props = withDefaults(defineProps<{ url?: string; uploadFile?: UploadFile; disabled?: boolean }>(), {
  url: undefined,
  uploadFile: undefined,
  disabled: false,
})
const emit = defineEmits(['clear'])

const loadingPdf = ref(false)
const networkError = ref<string | undefined>()

/**
 * Loads a raw Blob PDF file into the local pdfData ref
 * @param source the Blob file to load
 */
const loadPDFFile = (source: Blob) => {
  const reader = new FileReader()
  reader.onload = async () => {
    const pdfArray = new Uint8Array(reader.result as ArrayBuffer)
    pdfData.value = pdfArray
    pdfName.value = props.uploadFile?.name ?? urlDocumentTitle.value
    loadingPdf.value = false
  }
  reader.readAsArrayBuffer(source)
}

// Load the PDF file, either from source blob or by fetching the URL
onMounted(() => {
  if (props.uploadFile?.raw?.type == 'application/pdf') {
    loadingPdf.value = true
    loadPDFFile(props.uploadFile.raw)
  }

  if (!props.uploadFile && props.url) {
    loadingPdf.value = true

    axios
      .get(props.url, {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'application/pdf',
        },
      })
      .then((response) => new Blob([response.data], { type: 'application/pdf' }))
      .then(loadPDFFile)
      .catch((err) => {
        networkError.value = err.message
      })
  }
})

const clear = async () => {
  pdfData.value = undefined
  pdfName.value = undefined

  // Clear the pdf viewer
  // usePDF is just not reacting ¯\_(ツ)_/¯
  pdf.value = undefined
  info.value = {}

  emit('clear')
}
</script>

<template>
  <RlFileViewer v-loading="loadingPdf" :disabled="disabled" @clear="clear">
    <template #header>
      <pdfSvg class="shrink-0" :height="32" :width="32" viewBox="-2 0 24 24" />
      <div class="w-9/12">
        <p class="leading-5">{{ pdfName }}</p>
        <p v-if="documentTitle" class="text-tertiary text-xs m-0">{{ documentTitle }}</p>
      </div>
    </template>
    <VuePDF ref="vuePDFRef" :pdf="pdf" fit-parent />
    <el-alert v-if="networkError" :title="networkError" type="error" />
  </RlFileViewer>
</template>
